<template>
    <div class="text-gray-400 p-4">Nothing to display here</div>
</template>

<script setup lang="ts">
import {
    ImageMagick,
    MagickFormat,
    IMagickImage,
} from '@imagemagick/magick-wasm';
import { formats } from '~/helpers/data';
import { ConvertHeicResultMessage, FormatID } from '~/helpers/types';
import { blobToDataURL, isDevelopment } from '~/helpers/utils';

const onWindowMessage = async (e: MessageEvent) => {
    const request = e.data;
    if (request.message == 'convert_heic') {
        if (isDevelopment()) {
            console.log('[DEV] Received request to convert image');
        }

        const file = request.file;
        const itemId = request.itemId;
        const quality = request.quality;
        const formatId: FormatID = (request.format ?? 'jpg') as FormatID;

        const format = formats[formatId];

        // message to send back
        const message: ConvertHeicResultMessage = {
            message: 'convert_heic_result',
            itemId: itemId,
            format: formatId,
            url: '',
            error: '',
            errorObject: null as any,
        };

        const array = new Uint8Array(await file.arrayBuffer());

        const startTime = performance.now(); // Start profiling

        try {
            const blob = await new Promise((resolve) => {
                ImageMagick.read(array, (image: IMagickImage) => {
                    image.quality = quality;
                    let magicFormat;
                    switch (formatId) {
                        case 'jpg':
                            magicFormat = MagickFormat.Jpeg;
                            break;
                        case 'webp':
                            magicFormat = MagickFormat.WebP;
                            break;
                        case 'png':
                            magicFormat = MagickFormat.Png;
                            break;
                        default:
                            magicFormat = MagickFormat.Jpeg;
                            break;
                    }
                    image.write(magicFormat, (data) => {
                        const blob = new Blob([data], {
                            type: format.mime,
                        });
                        resolve(blob);
                    });
                });
            });
            const dataUri = await blobToDataURL(blob);
            message.url = dataUri;
        } catch (e) {
            // send result back
            message.error = 'could_not_convert';
            message.errorObject = e;
            message.url = file;
        }

        const endTime = performance.now(); // End profiling
        const timeTaken = endTime - startTime;
        const seconds = (timeTaken / 1000).toFixed(2);
        if (isDevelopment()) {
            console.log(`[DEV] Image conversion took ${seconds}s`);
        }

        // @ts-ignore
        e.source?.postMessage(message, e.origin);
    }
};

onMounted(() => {
    window.addEventListener('message', onWindowMessage, false);
});

onUnmounted(() => {
    window.removeEventListener('message', onWindowMessage, false);
});
</script>
